import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import PersistentLayout from '@/layouts/PersistentLayout'
import { initSentry } from '@/sentry'

initSentry()

// 予約問診フロー（interview_step1〜4）でブラウザバック時に
// Inertia の history-state cache から復元された後、サーバ最新の props を再取得する。
// useEffect ベースの popstate listener では発火しないため、モジュール top-level で
// 永続的に登録する（モジュールは Vite で1回しか評価されないため重複しない）。
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (window.location.pathname.startsWith('/online_clinic/reservations/interview_step')) {
      // Inertia の popstate handler が router.cancelAll() を呼ぶため、
      // setTimeout で defer して Inertia の処理後に visit を発火させる
      setTimeout(() => {
        router.visit(window.location.href, {
          preserveState: false,
          preserveScroll: true,
          replace: true,
        })
      }, 0)
    }
  })
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // PersistentLayoutをデフォルトレイアウトとして設定
    // see https://inertia-rails.dev/guide/pages#default-layouts
    page.default.layout ??= [PersistentLayout]

    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
